import classNames from "classnames"
import { ReactNode } from "react"
import { Icon, IconLibraryKeys } from "../Icon"
import {
  STATUS_BACKGROUND_COLORS,
  STATUS_ICON_COLORS,
  STATUS_TEXT_COLORS,
} from "../../constants/StatusColors"

export type AlertStatus =
  | "info"
  | "critical"
  | "success"
  | "attention"
  | "neutral"

interface Props {
  size?: "sm" | "lg"
  status: AlertStatus
  text?: string
  heading?: boolean
  children?: ReactNode
  icon?: IconLibraryKeys
}

type StatusMap<T> = { [key in AlertStatus]: T }

const iconMap: StatusMap<IconLibraryKeys> = {
  info: "Info",
  success: "CheckCircle",
  attention: "Exclamation",
  critical: "XCircle",
  neutral: "Printer",
}

export function Alert({
  status,
  text,
  size = "sm",
  children,
  heading,
  icon,
}: Props) {
  const containerClasses = classNames(
    { "p-[var(--component-alert-sm-padding)]": size === "sm" },
    { "p-[var(--component-alert-lg-padding)]": size === "lg" },
    "rounded-xl flex flex-row",
    {
      [STATUS_BACKGROUND_COLORS.Info]: status === "info",
      [STATUS_BACKGROUND_COLORS.Success]: status === "success",
      [STATUS_BACKGROUND_COLORS.Attention]: status === "attention",
      [STATUS_BACKGROUND_COLORS.Critical]: status === "critical",
      [STATUS_BACKGROUND_COLORS.Neutral]: status === "neutral",
    },
  )

  const iconClasses = classNames(
    {
      [STATUS_ICON_COLORS.Info]: status === "info",
      [STATUS_ICON_COLORS.Success]: status === "success",
      [STATUS_ICON_COLORS.Attention]: status === "attention",
      [STATUS_ICON_COLORS.Critical]: status === "critical",
      [STATUS_ICON_COLORS.Neutral]: status === "neutral",
    },
    "mr-1",
  )

  const textClasses = classNames(
    { "[font-size:var(--alias-font-caption-font-size)]": size === "sm" },
    { "[font-size:var(--alias-font-body-sm-base-font-size)]": size === "lg" },
    "[font-family:var(--alias-font-caption-font-family)]",
    {
      [STATUS_TEXT_COLORS.Info]: status === "info",
      [STATUS_TEXT_COLORS.Success]: status === "success",
      [STATUS_TEXT_COLORS.Attention]: status === "attention",
      [STATUS_TEXT_COLORS.Critical]: status === "critical",
      [STATUS_TEXT_COLORS.Neutral]: status === "neutral",
    },
  )

  const iconSize = size === "sm" ? "md" : "lg"

  return (
    <div
      aria-label={`The status of this error is ${status}`}
      className={containerClasses}
    >
      <div className="flex flex-col items-center">
        <Icon
          className={iconClasses}
          size={iconSize}
          variant={icon || iconMap[status]}
        />
      </div>
      <div className="flex flex-col ml-6">
        {text ? (
          <p
            className={`${textClasses} ${
              heading
                ? "[font-family:var(--alias-font-subtitle-lg-font-weight)]"
                : null
            }`}
          >
            {text}
          </p>
        ) : null}
        <div className={classNames(textClasses)}>{children}</div>
      </div>
    </div>
  )
}
